import React from 'react';
import { TARGET_MUSCLE_COLORS, displayMuscleName } from '../lib/muscleUtils';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * TargetMuscleBadges — Shows target muscle badges with set counts
 * Replaces old WorkSetsBadges that used broad muscle groups
 * @param {Object} stats — { pectorals: 10, quads: 8, biceps: 6, ... }
 * @param {string} size — 'xs' | 'sm'
 * @param {boolean} showSecondaryNote — show "incl. secondary" note
 */
export default function TargetMuscleBadges({ stats = {}, size = 'sm', showSecondaryNote = false }) {
  const { language } = useLanguage();
  const entries = Object.entries(stats)
    .filter(([_, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  
  if (entries.length === 0) return null;

  const textSize = size === 'xs' ? 'text-[9px]' : 'text-xs';
  const padding = size === 'xs' ? 'px-1.5 py-0' : 'px-1.5 py-0.5';

  // Format sets: show decimals only if they exist (from secondary credit)
  const formatSets = (v) => v % 1 === 0 ? v : v.toFixed(1);

  return (
    <div data-testid="target-muscle-badges">
      <div className="flex flex-wrap gap-1">
        {entries.map(([muscle, count]) => (
          <span
            key={muscle}
            className={`${textSize} ${padding} rounded-md border font-semibold ${TARGET_MUSCLE_COLORS[muscle] || 'bg-muted text-muted-foreground border-border/50'}`}
          >
            {displayMuscleName(muscle, language)} {formatSets(count)}
          </span>
        ))}
      </div>
      {showSecondaryNote && (
        <p className="text-[8px] text-muted-foreground mt-1 opacity-60">* secondary muscles count as 0.5x</p>
      )}
    </div>
  );
}
